<script lang="ts">
  import { onMount } from 'svelte';
  import HangmanFigure from './lib/HangmanFigure.svelte';
  import WordDisplay from './lib/WordDisplay.svelte';
  import Keyboard from './lib/Keyboard.svelte';
  import GameStatus from './lib/GameStatus.svelte';
  import GameSettings from './lib/GameSettings.svelte';
  import type { Difficulty, GameState, LetterState } from './lib/types';
  import { createGame, nextGame, makeGuess, getLetterState } from './lib/gameLogic';

  let gameState: GameState = createGame('medium');
  let difficulty: Difficulty = 'medium';
  let category: string = '';
  let letterStates: Record<string, LetterState> = {};
  let showSettings: boolean = true;
  let showConfetti: boolean = false;
  let newGameFlash: boolean = false;

  let physicalKeyboardEnabled: boolean = true;

  function updateLetterStates() {
    const states: Record<string, LetterState> = {};
    for (const letter of 'abcdefghijklmnopqrstuvwxyz') {
      states[letter] = getLetterState(letter, gameState.word, gameState.guessedLetters);
    }
    letterStates = states;
  }

  function handleGuess(event: CustomEvent<string>) {
    const letter = event.detail;
    if (gameState.status !== 'playing') return;
    gameState = makeGuess(gameState, letter);
    updateLetterStates();

    if (gameState.status === 'won') {
      showConfetti = true;
      setTimeout(() => (showConfetti = false), 3000);
    }
  }

  function handlePlayAgain() {
    const won = gameState.status === 'won';
    gameState = nextGame(gameState, difficulty, category);
    if (won) gameState = { ...gameState, gamesWon: gameState.gamesWon + 1 };
    updateLetterStates();
  }

  function handleNewGame() {
    newGameFlash = true;
    gameState = nextGame(gameState, difficulty, category);
    updateLetterStates();
    setTimeout(() => (newGameFlash = false), 300);
  }

  function handleDifficultyChange(event: CustomEvent<string>) {
    difficulty = event.detail as Difficulty;
    gameState = createGame(difficulty, category);
    updateLetterStates();
  }

  function handleCategoryChange(event: CustomEvent<string>) {
    category = event.detail;
    gameState = createGame(difficulty, category);
    updateLetterStates();
  }

  function handlePhysicalKeydown(e: KeyboardEvent) {
    if (!physicalKeyboardEnabled) return;
    const letter = e.key.toLowerCase();
    if (!/^[a-z]$/.test(letter)) return;
    if (gameState.guessedLetters.has(letter)) return;
    if (gameState.status !== 'playing') return;
    gameState = makeGuess(gameState, letter);
    updateLetterStates();

    if (gameState.status === 'won') {
      showConfetti = true;
      setTimeout(() => (showConfetti = false), 3000);
    }
  }

  onMount(() => {
    window.addEventListener('keydown', handlePhysicalKeydown);
    return () => {
      window.removeEventListener('keydown', handlePhysicalKeydown);
    };
  });

  $: if (gameState) {
    updateLetterStates();
  }
</script>

<div class="app-container">
  <!-- Header -->
  <header class="app-header">
    <h1 class="app-title">
      <span class="title-icon">🎭</span>
      Hangman
    </h1>
    <p class="app-subtitle">Guess the word before it's too late!</p>
    <div class="stats-bar">
      <div class="stat">
        <span class="stat-icon">🎮</span>
        <span class="stat-label">Games</span>
        <span class="stat-value">{gameState.gamesPlayed}</span>
      </div>
      <div class="stat">
        <span class="stat-icon">🏆</span>
        <span class="stat-label">Won</span>
        <span class="stat-value">{gameState.gamesWon}</span>
      </div>
      <div class="stat">
        <span class="stat-icon">⭐</span>
        <span class="stat-label">Score</span>
        <span class="stat-value">{gameState.score}</span>
      </div>
      <div class="stat">
        <span class="stat-icon">❌</span>
        <span class="stat-label">Wrong</span>
        <span class="stat-value">{gameState.wrongGuesses}/{gameState.maxWrongGuesses}</span>
      </div>
    </div>
  </header>

  <!-- Settings -->
  {#if showSettings}
    <div class="settings-section">
      <GameSettings
        {difficulty}
        {category}
        {newGameFlash}
        on:difficultyChange={handleDifficultyChange}
        on:categoryChange={handleCategoryChange}
        on:newGame={handleNewGame}
      />
    </div>
  {/if}

  <!-- Main Game Area -->
  <main class="game-area">
    <div class="game-layout">
      <!-- Hangman Figure (top on mobile, left on desktop) -->
      <div class="figure-section">
        <HangmanFigure
          wrongGuesses={gameState.wrongGuesses}
          maxWrong={gameState.maxWrongGuesses}
        />
      </div>

      <!-- Word Display -->
      <div class="word-section">
        <WordDisplay
          word={gameState.word}
          guessedLetters={gameState.guessedLetters}
          revealAll={gameState.status === 'lost'}
        />
      </div>

      <!-- Status (win/lose) or Keyboard -->
      {#if gameState.status !== 'playing'}
        <div class="status-section">
          <GameStatus
            status={gameState.status}
            word={gameState.word}
            score={gameState.score}
            on:playAgain={handlePlayAgain}
            on:newGame={handleNewGame}
          />
        </div>
      {:else}
        <div class="keyboard-section">
          <Keyboard
            {letterStates}
            disabled={gameState.status !== 'playing'}
            on:guess={handleGuess}
          />
          <p class="keyboard-hint">
            <span class="hint-icon">⌨️</span>
            or type a letter on your keyboard
          </p>
        </div>
      {/if}
    </div>
  </main>

  <!-- Footer -->
  <footer class="app-footer">
    <div class="footer-content">
      <div class="footer-brand">
        <a
          href="https://github.com/TwitchCaptain/hangman"
          target="_blank"
          rel="noopener noreferrer"
          class="footer-logo-link"
          aria-label="View source on GitHub"
        >
          <img
            src="https://docs.golift.io/apple-touch-icon.png"
            alt="Go Lift Logo"
            class="footer-logo"
            width="24"
            height="24"
          />
        </a>
        <span class="footer-text">
          &copy; {new Date().getFullYear()}
          <a
            href="https://golift.io"
            target="_blank"
            rel="noopener noreferrer"
            class="footer-link"
          >Go Lift Technologies LLC</a>
        </span>
      </div>
      <div class="footer-links">
        <a
          href="https://github.com/TwitchCaptain/hangman"
          target="_blank"
          rel="noopener noreferrer"
          class="footer-link"
        >
          <svg class="footer-icon" viewBox="0 0 16 16" width="14" height="14" fill="currentColor"><path d="M8 0C3.58 0 0 3.58 0 8c0 3.54 2.29 6.53 5.47 7.59.4.07.55-.17.55-.38 0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82.64-.18 1.32-.27 2-.27s1.36.09 2 .27c1.53-1.04 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48 0 1.07-.01 1.93-.01 2.2 0 .21.15.46.55.38A8.013 8.013 0 0 0 16 8c0-4.42-3.58-8-8-8z"/></svg> Source
        </a>
      </div>
    </div>
  </footer>

  <!-- Confetti overlay -->
  {#if showConfetti}
    <div class="confetti-overlay">
      {#each Array(30) as _, i}
        <div
          class="confetti-piece"
          style="
            left: {Math.random() * 100}%;
            animation-delay: {Math.random() * 0.5}s;
            animation-duration: {1 + Math.random() * 2}s;
            background: {['#ff6b6b', '#feca57', '#48dbfb', '#ff9ff3', '#54a0ff', '#5aeba3', '#ff6348'][i % 7]};
          "
        />
      {/each}
    </div>
  {/if}
</div>

<style>
  :global(*) {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }

  :global(:root) {
    --bg-primary: #1a1a2e;
    --bg-secondary: #16213e;
    --bg-card: #0f3460;
    --text-primary: #e8e8e8;
    --text-secondary: #a8a8b8;
    --accent: #4a90d9;
    --accent-hover: #357abd;
    --correct-bg: #27ae60;
    --wrong-bg: #e74c3c;
    --figure-stroke: #e74c3c;
    --tile-bg: #2c3e50;
    --tile-border: #3d5a6e;
    --tile-color: #e8e8e8;
    --tile-hover-border: #4a90d9;
    --slot-bg: #1a1a2e;
    --slot-border: #3d5a6e;
    --slot-revealed-bg: #1b4332;
    --slot-revealed-border: #27ae60;
    --slot-hidden-bg: #2c3e50;
    --slot-hidden-border: #3d5a6e;
    --slot-char-color: #e8e8e8;
    --slot-blank-color: #6c7a89;
    --panel-bg: #16213e;
    --panel-border: #0f3460;
    --focus-ring: #4a90d9;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
    color: var(--text-primary);
    background: var(--bg-primary);
  }

  :global(body) {
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .app-container {
    max-width: 1100px;
    width: 100%;
    padding: 16px;
    margin: 0 auto;
    min-height: 100vh;
    display: flex;
    flex-direction: column;
  }

  .app-header {
    text-align: center;
    padding: 12px 0;
  }

  .app-title {
    font-size: 32px;
    font-weight: 800;
    letter-spacing: -1px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
  }

  .title-icon {
    font-size: 28px;
  }

  .app-subtitle {
    color: var(--text-secondary);
    font-size: 13px;
    margin-top: 2px;
  }

  .stats-bar {
    display: flex;
    justify-content: center;
    gap: 12px;
    margin-top: 10px;
    flex-wrap: wrap;
  }

  .stat {
    display: flex;
    align-items: center;
    gap: 4px;
    background: var(--bg-card);
    padding: 6px 12px;
    border-radius: 8px;
    font-size: 12px;
  }

  .stat-icon {
    font-size: 14px;
  }

  .stat-label {
    color: var(--text-secondary);
    font-weight: 500;
  }

  .stat-value {
    font-weight: 700;
    color: var(--accent);
  }

  .settings-section {
    margin: 6px 0;
  }

  .game-area {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 0;
  }

  .game-layout {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    width: 100%;
    max-width: 600px;
  }

  .figure-section {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    max-width: 200px;
  }

  .word-section {
    background: var(--bg-card);
    border-radius: 12px;
    padding: 8px;
    width: 100%;
  }

  .status-section {
    width: 100%;
    margin-top: 4px;
  }

  .keyboard-section {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
    width: 100%;
  }

  .keyboard-hint {
    font-size: 11px;
    color: var(--text-secondary);
    display: flex;
    align-items: center;
    gap: 4px;
  }

  .hint-icon {
    font-size: 12px;
  }

  .app-footer {
    padding: 12px 0;
    color: var(--text-secondary);
    font-size: 12px;
    margin-top: auto;
  }

  .footer-content {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: center;
    gap: 12px;
  }

  .footer-brand {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .footer-logo {
    display: block;
    border-radius: 4px;
    transition: opacity 0.2s;
  }

  .footer-logo:hover {
    opacity: 0.8;
  }

  .footer-logo-link {
    display: flex;
    align-items: center;
    text-decoration: none;
    color: inherit;
  }

  .footer-text {
    color: var(--text-secondary);
  }

  .footer-links {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .footer-link {
    color: var(--accent);
    text-decoration: none;
    transition: color 0.2s;
  }

  .footer-link:hover {
    color: var(--accent-hover);
    text-decoration: underline;
  }

  .footer-icon {
    display: inline-block;
    vertical-align: middle;
    margin-right: 2px;
  }

  /* Confetti */
  .confetti-overlay {
    position: fixed;
    inset: 0;
    pointer-events: none;
    z-index: 1000;
    overflow: hidden;
  }

  .confetti-piece {
    position: absolute;
    top: -10px;
    width: 10px;
    height: 10px;
    border-radius: 2px;
    animation: confettiFall 2s ease-in-out forwards;
    transform: rotate(45deg);
  }

  @keyframes confettiFall {
    0% {
      transform: translateY(0) rotate(45deg);
      opacity: 1;
    }
    100% {
      transform: translateY(100vh) rotate(360deg);
      opacity: 0;
    }
  }

  /* Desktop: side-by-side layout */
  @media (min-width: 769px) {
    .game-layout {
      display: grid;
      grid-template-columns: 280px 1fr;
      grid-template-rows: auto auto;
      gap: 24px;
      max-width: 900px;
      align-items: start;
    }

    .figure-section {
      grid-column: 1;
      grid-row: 1 / 3;
      max-width: 100%;
    }

    .word-section {
      grid-column: 2;
      grid-row: 1;
    }

    .status-section,
    .keyboard-section {
      grid-column: 2;
      grid-row: 2;
    }

    .app-title {
      font-size: 36px;
    }

    .stats-bar {
      gap: 24px;
    }

    .stat {
      padding: 8px 16px;
      font-size: 14px;
    }
  }
</style>
